import { useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { type DocNode } from '@atlaskit/adf-schema/doc';
import { type UnconnectedApp } from '@atlassian/conversation-assistant-chat-prompt-input/app-connection-banner';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { useConnectors } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../controllers/conversation/useConversation';

const messages = defineMessages({
	connectorConnectedConfirmationLead: {
		id: 'ai-mate.conversation-assistant.connection-prompt.connector-connected-confirmation-lead',
		defaultMessage: '{appName} is connected!',
		description:
			'Bold lead sentence of the assistant message appended after the user successfully connects a third-party app from the in-chat connection prompt',
	},
	connectorConnectedConfirmationBody: {
		id: 'ai-mate.conversation-assistant.connection-prompt.connector-connected-confirmation-body',
		defaultMessage:
			'I am indexing your team’s data so context can flow into your new docs automatically. This usually takes a few hours to fully set up. Need something right now? Paste a direct link and I’ll use it instantly.',
		description:
			'Body of the assistant message appended after the user successfully connects a third-party app from the in-chat connection prompt',
	},
});

/**
 * Reacts to the user finishing a third-party connection from the in-chat
 * connection prompt with two effects, both keyed off the same
 * observed `false → true` `isUserOAuthed` transition:
 *   1. Appends a one-off assistant confirmation message (mirrors
 *      `useForgeAuthController`'s post-auth message injection).
 *   2. Enables the just-connected app in the sources filter so the next message
 *      is grounded in it — `addAppFilter` no-ops unless the filter
 *      is narrowed, so it never turns "all apps" into "only this app".
 *
 * Driven by the caller's pinned `apps` list (not the transient banner store), so
 * it keeps working after the banner store is cleared. We fire only on the
 * transition for an app in the list, so an already-connected app never triggers
 * either effect and each connect produces exactly one confirmation.
 */
export const usePromptConnectorConnectedEffects = ({
	conversationId,
	apps,
}: {
	conversationId: string;
	apps: UnconnectedApp[];
}): void => {
	const [connectors] = useConnectors();
	const { addAgentMessage } = useChatStoreActions();
	const { addAppFilter } = useChatModeStoreActions();
	const { conversation, selectedAgent } = useConversation({ conversationId });
	const { formatMessage } = useIntl();

	// Last-seen OAuth state per app source; presence of a key means "observed
	// before", so we can distinguish an initial reading from a real transition.
	const prevOAuthedRef = useRef<Record<string, boolean>>({});

	// Drop the transition history when the conversation changes.
	useEffect(() => {
		prevOAuthedRef.current = {};
	}, [conversationId]);

	useEffect(() => {
		if (apps.length === 0 || !conversation || !selectedAgent) {
			return;
		}

		for (const app of apps) {
			const isOAuthed = Boolean(connectors[app.source]?.isUserOAuthed);
			const seenBefore = Object.prototype.hasOwnProperty.call(prevOAuthedRef.current, app.source);
			const wasOAuthed = Boolean(prevOAuthedRef.current[app.source]);

			if (seenBefore && isOAuthed && !wasOAuthed) {
				// ADF (not a plain string) so the lead sentence can be bold.
				const content = {
					version: 1,
					type: 'doc',
					content: [
						{
							type: 'paragraph',
							content: [
								{
									type: 'text',
									text: formatMessage(messages.connectorConnectedConfirmationLead, {
										appName: app.friendlyName,
									}),
									marks: [{ type: 'strong' }],
								},
								{
									type: 'text',
									text: ` ${formatMessage(messages.connectorConnectedConfirmationBody)}`,
								},
							],
						},
					],
				} as DocNode;

				addAgentMessage({
					conversationId: conversation.id,
					agentNamedId: selectedAgent.named_id,
					content,
				});

				// Keyed by `conversationId` (the prompt-input key the mode store uses),
				// not `conversation.id` above (the chat store's key). They match once the
				// conversation is persisted, which it always is here — the prompt only
				// renders after the first assistant response completes.
				addAppFilter(conversationId, {
					source: app.source,
					friendlyName: app.friendlyName,
					iconUrl: app.iconUrl,
				});
			}
		}

		const snapshot: Record<string, boolean> = {};
		for (const app of apps) {
			snapshot[app.source] = Boolean(connectors[app.source]?.isUserOAuthed);
		}
		prevOAuthedRef.current = snapshot;
	}, [
		conversationId,
		apps,
		connectors,
		conversation,
		selectedAgent,
		addAgentMessage,
		addAppFilter,
		formatMessage,
	]);
};
